import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  NotificationHistoryQuery,
  NotificationHistoryState,
} from "./models/notificationModels";
import notificationsApi from "./api/notificationsApi";

const initialQuery: NotificationHistoryQuery = {
  pageNo: 1,
  pageSize: 10,
  sortDesc: true,
};

const initialState: NotificationHistoryState = {
  query: initialQuery,
  notifications: null,
  loading: false,
  error: undefined,
};

export const fetchNotificationHistory = createAsyncThunk(
  "notificationHistory/fetch",
  async (query: NotificationHistoryQuery) => {
    return notificationsApi.searchNotifications(query);
  }
);

export const markHistoryNotificationAsRead = createAsyncThunk(
  "notificationHistory/markAsRead",
  async (notificationId: string) => {
    await notificationsApi.markAsRead(notificationId);
    return notificationId;
  }
);

export const markAllHistoryAsRead = createAsyncThunk(
  "notificationHistory/markAllAsRead",
  async () => {
    await notificationsApi.markAllAsRead();
  }
);

export const deleteHistoryNotification = createAsyncThunk(
  "notificationHistory/delete",
  async (notificationId: string) => {
    await notificationsApi.deleteNotification(notificationId);
    return notificationId;
  }
);

const notificationHistorySlice = createSlice({
  name: "notificationHistory",
  initialState,
  reducers: {
    changeHistoryQuery: (
      state,
      action: PayloadAction<NotificationHistoryQuery>
    ) => {
      state.query = action.payload;
    },
    clearHistoryError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationHistory.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchNotificationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchNotificationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(
        markHistoryNotificationAsRead.fulfilled,
        (state, action) => {
          const notification = state.notifications?.items?.find(
            (n) => n.id === action.payload
          );
          if (notification) {
            notification.isRead = true;
          }
        }
      )
      .addCase(markAllHistoryAsRead.fulfilled, (state) => {
        state.notifications?.items?.forEach((n) => {
          n.isRead = true;
        });
      })
      .addCase(deleteHistoryNotification.fulfilled, (state, action) => {
        if (state.notifications?.items) {
          state.notifications.items = state.notifications.items.filter(
            (n) => n.id !== action.payload
          );
          if (state.notifications.totalCount > 0) {
            state.notifications.totalCount -= 1;
          }
        }
      });
  },
});

export const { changeHistoryQuery, clearHistoryError } =
  notificationHistorySlice.actions;

export default notificationHistorySlice.reducer;
